import { useEffect, useState } from "react";

import { Button, Container, Grid, Typography, useTheme } from "@mui/material";
import { AnimatedLogo } from "@aclymatepackages/atoms";

import Link from "../atoms/mui/Link";
import { fetchOurApi } from "../../helpers/utils/apiCalls";
import { getUrlSearchParams } from "../../helpers/otherHelpers";

const UserVerification = () => {
  const [status, setStatus] = useState("denied");
  const [dataLoading, setDataLoading] = useState(false);

  useEffect(() => {
    const rawAdminResponse = getUrlSearchParams("value");
    const adminResponse = rawAdminResponse === "true";
    const token = getUrlSearchParams("token");

    const sendAcceptanceEmailToUser = async ({ companyId, userEmail }) => {
      return await fetchOurApi({
        path: "/emails/send-acceptance-email",
        method: "POST",
        data: { userEmail, companyId },
      });
    };

    const addUser = async () => {
      return await fetchOurApi({
        path: "/users/add-user-by-admin",
        method: "POST",
        data: { token },
      });
    };

    const rejectUser = async () => {
      return await fetchOurApi({
        path: "/users/reject-admin-request",
        method: "POST",
        data: { token },
      });
    };

    const handleUserVerification = async () => {
      if (!adminResponse) {
        await rejectUser();
        return setStatus("denied");
      }
      // The backend can revert a failed grant back to a retryable "pending"
      // state, so a failure here means the token is safe to click again --
      // don't tell the admin it succeeded until addUser() actually confirms
      // it did.
      const { companyId, userEmail } = (await addUser()) || {};
      if (!companyId) {
        return setStatus("error");
      }
      setStatus("granted");
      return await sendAcceptanceEmailToUser({ companyId, userEmail });
    };

    const run = async () => {
      if (rawAdminResponse === null) {
        return;
      }
      setDataLoading(true);
      // Awaited so dataLoading (and the AnimatedLogo it shows) actually
      // covers the async grant/reject call -- otherwise the page falls
      // through to the status message before status has been set to
      // anything but its stale default.
      await handleUserVerification();
      setDataLoading(false);
    };

    run();
  }, []);

  const { palette } = useTheme();

  if (dataLoading) {
    return <AnimatedLogo />;
  }

  const statusMessages = {
    denied: "You denied this user access to your account",
    granted: "This user has been granted access to your company account",
    error:
      "Something went wrong granting access. Please click the link in the email again to retry."
  };

  return (
    <Container maxWidth="md" style={{ height: "100vh" }}>
      <Grid
        container
        direction="column"
        spacing={2}
        alignItems="center"
        justifyContent="center"
        style={{ height: "100%" }}
      >
        <Grid item>
          <Typography
            variant="h5"
            color={status === "error" ? palette.error.main : "textPrimary"}
          >
            {statusMessages[status]}
          </Typography>
        </Grid>
        <Grid item>
          <Typography variant="h6" color="textSecondary">
            You can close the window
          </Typography>
        </Grid>
        <Grid item>
          <Link href="https://aclymate.app/login">
            <Button variant="contained">Login</Button>
          </Link>
        </Grid>
      </Grid>
    </Container>
  );
};

export default UserVerification;
